import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  ButtonBase,
} from '@mui/material'
import axios from 'axios'

import { DashboardMedia } from '../../../types/gotypes'
import { API_BASE_URL } from '../../../constants/system'
import { useMediaUrl } from '../../../hooks/useMediaUrl'

// #1070 — job-scoped media library picker. Additive and self-contained on
// purpose (kept out of ImageWidget.tsx beyond a single hook-up point) since
// the image widget's fit/alignment controls are being edited in parallel.

interface DashboardMediaThumbnailProps {
  customer: string
  media: DashboardMedia
  selected: boolean
  onClick: () => void
}

const DashboardMediaThumbnail: React.FC<DashboardMediaThumbnailProps> = ({
  customer,
  media,
  selected,
  onClick,
}) => {
  const { url, loading } = useMediaUrl(customer, media.job, media.blob_path)

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 140,
        height: 140,
        border: '2px solid',
        borderColor: selected ? 'info.main' : 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: 'background.paper',
      }}
    >
      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <CircularProgress size={20} />
        </Box>
      ) : url ? (
        <Box
          component="img"
          src={url}
          alt={media.filename}
          sx={{ flex: 1, width: '100%', objectFit: 'contain', minHeight: 0 }}
        />
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <Typography variant="caption" color="text.secondary">
            No preview
          </Typography>
        </Box>
      )}
      <Typography
        variant="caption"
        sx={{
          width: '100%',
          px: 0.5,
          py: 0.25,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        {media.filename}
      </Typography>
    </ButtonBase>
  )
}

interface DashboardMediaPickerProps {
  customer: string
  job: string
  open: boolean
  onClose: () => void
  onSelect: (media: DashboardMedia) => void
}

export const DashboardMediaPicker: React.FC<DashboardMediaPickerProps> = ({
  customer,
  job,
  open,
  onClose,
  onSelect,
}) => {
  const [media, setMedia] = useState<DashboardMedia[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Lifecycle-tied fetch: load the job's media whenever the picker opens (or
  // the job it's scoped to changes while open). Not a state mirror — this is
  // the fetch that populates the dialog's content.
  useEffect(() => {
    if (!open || !customer || !job) return

    let cancelled = false
    setLoading(true)
    setError(null)

    axios
      .get(`${API_BASE_URL}/dashboards/${customer}/media`, { params: { job } })
      .then((res) => {
        if (cancelled) return
        setMedia(res.data.media || [])
      })
      .catch((err) => {
        if (cancelled) return
        const message =
          axios.isAxiosError(err) && err.response?.data?.error
            ? err.response.data.error
            : err instanceof Error
              ? err.message
              : 'Failed to load media library'
        setError(message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, customer, job])

  const handleConfirm = () => {
    const chosen = media.find((m) => m.id === selectedId)
    if (chosen) {
      onSelect(chosen)
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Choose an image from this job&apos;s library</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : media.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No images uploaded for this job yet. Upload one to get started.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, py: 1 }}>
            {media.map((m) => (
              <DashboardMediaThumbnail
                key={m.id}
                customer={customer}
                media={m}
                selected={m.id === selectedId}
                onClick={() => setSelectedId(m.id)}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="info" onClick={handleConfirm} disabled={!selectedId}>
          Use Selected Image
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DashboardMediaPicker
